import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../core/redux/store";
import { Spinner } from "react-bootstrap";
import { utils } from "../core/helpers";
import ProfileCard from "../feature-module/Instructor/common/profileCard";
import { Link } from "react-router-dom";
import {
  fetchClassrooms,
  createClassroom,
  updateClassroom,
  deleteClassroom,
} from "../core/redux/classroomSlice";

const StudentGroups: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const {
    list: classrooms,
    loading,
    creating,
    updating,
    deleting,
    error,
  } = useSelector((state: RootState) => state.classrooms);
  const { levels } = useSelector((state: RootState) => state.levels);
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [selectedLevelIds, setSelectedLevelIds] = useState<number[]>([]);
  const [editGroupName, setEditGroupName] = useState<string>("");
  const [editLevelIds, setEditLevelIds] = useState<number[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const wasCreating = useRef(false);
  const wasUpdating = useRef(false);

  useEffect(() => {
    if (!(!!utils.getPasswordToken() && !!user)) {
      navigate("/login");
      return;
    }
  }, [navigate, user]);

  useEffect(() => {
    dispatch(fetchClassrooms());
  }, [dispatch]);

  // Close Add modal on successful create
  useEffect(() => {
    if (wasCreating.current && !creating) {
      if (!error) {
        (document.querySelector("#add_quiz .btn-close") as HTMLButtonElement)?.click();
        setNewGroupName("");
        setSelectedLevelIds([]);
      }
    }
    wasCreating.current = creating;
  }, [creating, error]);

  // Close Edit modal on successful update
  useEffect(() => {
    if (wasUpdating.current && !updating) {
      if (!error) {
        (document.querySelector("#edit_quiz .btn-close") as HTMLButtonElement)?.click();
        setSelectedId(null);
      }
    }
    wasUpdating.current = updating;
  }, [updating, error]);

  const onOpenEdit = (id: number, name: string, levelIds: number[] = []) => {
    setSelectedId(id);
    setEditGroupName(name);
    setEditLevelIds(levelIds);
  };

  const onOpenDelete = (id: number) => {
    setSelectedId(id);
  };

  const handleCreate = () => {
    if (!newGroupName.trim()) return;
    dispatch(createClassroom({ 
      name: newGroupName.trim(),
      level_ids: selectedLevelIds.length > 0 ? selectedLevelIds : undefined
    }));
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleCreate();
  };

  const handleLevelChange = (levelId: number) => {
    setSelectedLevelIds((prev) =>
      prev.includes(levelId)
        ? prev.filter((id) => id !== levelId)
        : [...prev, levelId]
    );
  };

  const handleEditLevelChange = (levelId: number) => {
    setEditLevelIds((prev) =>
      prev.includes(levelId)
        ? prev.filter((id) => id !== levelId)
        : [...prev, levelId]
    );
  };

  const handleUpdate = () => {
    if (!selectedId || !editGroupName.trim()) return;
    dispatch(updateClassroom({ 
      id: selectedId, 
      name: editGroupName.trim(),
      level_ids: editLevelIds.length > 0 ? editLevelIds : undefined
    }));
  };

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleUpdate();
  };

  const handleDelete = () => {
    if (!selectedId) return;
    dispatch(deleteClassroom({ id: selectedId }));
  };

  // if (loading || categories.length === 0) {
  //   return <Spinner />;
  // }

  return (
    <>
      {/* <Breadcrumb title="Student Groups" /> */}
      <div className="content">
        <div className="container">
          <ProfileCard buttonTitle={"Home Page"} />
          <div className="row">
            <div className="col-lg-12">
              <div className="page-title d-flex align-items-center justify-content-between">
                <h5 className="fw-bold">Cohorts</h5>
                <div>
                  <Link
                    to="#"
                    className="btn btn-secondary"
                    data-bs-toggle="modal"
                    data-bs-target="#add_quiz"
                  >
                    <i className="isax isax-add me-2"></i>
                    Create Cohort
                  </Link>
                </div>
              </div>
              {loading ? (
                <div className="d-flex justify-content-center my-4">
                  <Spinner />
                </div>
              ) : classrooms && classrooms.length > 0 ? (
                classrooms.map((cls) => (
                  <div key={cls.id} className="border rounded-2 p-3 mb-3">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div>
                          <h6 className="mb-2">
                            <Link to={`/student-groups/${cls.id}/students`}>{cls.name}</Link>
                          </h6>
                          <div className="question-info d-flex align-items-center">
                            <p className="d-flex align-items-center fs-14 me-2 pe-2 mb-0">
                              <i className="isax isax-profile-2user text-primary-soft me-2"></i>
                              {cls.users_count} Student
                              {cls.users_count !== 1 ? "s" : ""}
                            </p>
                            <p className="d-flex align-items-center fs-14 me-2 pe-2 mb-0">
                              <i className="isax isax-book text-primary-soft me-2"></i>
                              {cls.levels.map(l => l.name).join(", ")}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                          <Link
                            to={`/student-groups/${cls.id}/students`}
                            className="fs-14 me-3 text-success"
                          >
                            <i className="isax isax-eye"></i> Students
                          </Link>
                          <Link
                            to={`/student-groups/${cls.id}/contents`}
                            className="fs-14 me-3 text-success"
                          >
                            <i className="isax isax-book"></i> Contents
                          </Link>
                          <Link
                            to="#"
                            className="fs-14 me-3 text-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#edit_quiz"
                            onClick={() => onOpenEdit(cls.id, cls.name, cls.levels?.map(l => l.id) || [])}
                          >
                            <i className="isax isax-edit-2"></i> Edit
                          </Link>
                          <Link
                            to="#"
                            className="fs-14 text-danger"
                            data-bs-toggle="modal"
                            data-bs-target="#delete_modal"
                            onClick={() => onOpenDelete(cls.id)} 
                          >
                            <i className="isax isax-trash"></i> Delete
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted my-4">
                  No cohorts found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Add Student Group */}
      <div className="modal fade" id="add_quiz" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="fw-bold">Create Cohort</h5>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="isax isax-close-circle5" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="modal-body pb-0">
                <div className="mb-3">
                  <label className="form-label">
                    Cohort Name <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Levels
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {levels.map((level) => (
                      <div key={level.value} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={level.value}
                          id={`level-${level.value}`}
                          checked={selectedLevelIds.includes(parseInt(level.value))}
                          onChange={() => handleLevelChange(parseInt(level.value))}
                        />
                        <label className="form-check-label" htmlFor={`level-${level.value}`}>
                          {level.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <span className="text-muted fs-12">
                    The contents of selected levels will be attached to this cohort automatically.
                  </span>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn bg-gray-100 rounded-pill me-2"
                  type="button"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  className="btn btn-secondary rounded-pill"
                  type="submit"
                  disabled={creating || !newGroupName.trim()}
                >
                  {creating ? (
                    <>
                      Adding...
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="ms-2"
                      />
                    </>
                  ) : (
                    "Create Cohort"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Student Group */}
      {/* Edit Student Group */}
      <div className="modal fade" id="edit_quiz" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="fw-bold">Edit Cohort</h5>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="isax isax-close-circle5" />
              </button>
            </div>
            <form onSubmit={handleUpdateSubmit}>
              <div className="modal-body pb-0">
                <div className="mb-3">
                  <label className="form-label">
                    Cohort Name <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editGroupName}
                    onChange={(e) => setEditGroupName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Levels
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {levels.map((level) => (
                      <div key={level.value} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={level.value}
                          id={`edit-level-${level.value}`}
                          checked={editLevelIds.includes(parseInt(level.value))}
                          onChange={() => handleEditLevelChange(parseInt(level.value))}
                        />
                        <label className="form-check-label" htmlFor={`edit-level-${level.value}`}>
                          {level.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <span className="text-muted fs-12">
                    The contents of selected levels will be attached to this cohort automatically.
                  </span>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn bg-gray-100 rounded-pill me-2"
                  type="button"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  className="btn btn-secondary rounded-pill"
                  type="submit"
                  disabled={updating || !editGroupName.trim()}
                >
                  {updating ? (
                    <>
                      Saving...
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="ms-2"
                      />
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Student Group */}
      {/* Delete Modal */}
      <div className="modal fade" id="delete_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center custom-modal-body">
              <span className="avatar avatar-lg bg-danger-transparent rounded-circle mb-2">
                <i className="isax isax-trash fs-24 text-danger" />
              </span>
              <div>
                <h4 className="mb-2">Delete Cohort</h4>
                <p className="mb-3">
                  Are you sure you want to delete this group?
                </p>
                <div className="d-flex align-items-center justify-content-center">
                  <Link
                    to="#"
                    className="btn bg-gray-100 rounded-pill me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link
                    to="#"
                    className="btn btn-secondary rounded-pill"
                    data-bs-dismiss="modal"
                    onClick={handleDelete}
                    aria-disabled={deleting}
                  >
                    Yes, Delete
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Delete Modal */}
    </>
  );
};

export default StudentGroups;
